import { cn } from "@/lib/utils";
import type { AutoCandidate } from "@opus-dt/shared-types";
import type { ObjectiveAxis } from "../objective/objective-meta";

// O que sustenta a leitura dos gráficos do ranking: a legenda das cores e a nota que
// desarma a confusão mais provável da tela (achar que a coluna de OEE e a do objetivo se somam).

/** Cor nunca sozinha: cada tom é nomeado aqui, e o valor vai escrito ao lado de cada barra. */
export function RankingLegend({ hasFailed }: { hasFailed: boolean }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-text-secondary">
      <LegendItem tone="bg-rd-success-500">melhor que a base</LegendItem>
      <LegendItem tone="bg-rd-danger-500">pior que a base</LegendItem>
      {hasFailed ? (
        <LegendItem tone="bg-bg-muted-rd border border-border-default">não avaliado</LegendItem>
      ) : null}
    </ul>
  );
}

function LegendItem({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <li className="inline-flex items-center gap-1.5">
      <span className={cn("h-2.5 w-2.5 shrink-0 rounded-[3px]", tone)} aria-hidden />
      {children}
    </li>
  );
}

interface RankingOeeNoteProps {
  ranked: AutoCandidate[];
  axis: ObjectiveAxis;
  showOee: boolean;
}

/**
 * A nota é ESCRITA a partir dos dados, não fixa: quando o maior OEE não é o primeiro colocado, a
 * tela precisa dizer isso em voz alta — é a hora em que alguém "corrige" o ranking pelo indicador
 * errado. Quando o maior OEE é o próprio vencedor, a nota confirma em vez de alertar.
 */
export function RankingOeeNote({ ranked, axis, showOee }: RankingOeeNoteProps) {
  const topOee = ranked.reduce<AutoCandidate | null>(
    (best, c) => (c.oee != null && (best?.oee == null || c.oee > best.oee) ? c : best),
    null
  );

  return (
    <p className="border-rd-info-500/30 rounded-md border bg-rd-info-50 p-3 text-[11px] leading-relaxed text-rd-info-600">
      {showOee && topOee?.oee != null ? (
        <b>
          {topOee.label ?? "O candidato"} tem o maior OEE ({topOee.oee.toFixed(1)}%) e{" "}
          {topOee.rankPosition === 1
            ? "também lidera no objetivo"
            : `ficou em ${topOee.rankPosition ?? "—"}º`}
          .{" "}
        </b>
      ) : null}
      {showOee ? (
        <>
          O objetivo aqui é {axis.short.toLowerCase()}, não OEE — por isso as duas colunas existem e
          não se somam.{" "}
        </>
      ) : (
        <>O objetivo aqui é o próprio OEE — a faixa acima já é a que ranqueia. </>
      )}
      Leia OEE <b>e</b> confiança juntos.
    </p>
  );
}
